import React from "react";
import {
  Form, Input, Button, Select, Card, Typography, InputNumber,
  Row, Col, Tooltip, Modal, Checkbox, Tag, message, Divider,
} from "antd";
import { InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import { Booking } from "../../types/booking";
import {
  COMMODITY_INSPECTION_FEE_EUR,
  getEffectiveCommodityInspection,
} from "../../utils/commodityInspectionUtils";
import { getPostExportLicenseFee } from "../../utils/postExportLicenseUtils";
import {
  FormValues, InvoiceLine, ContainerSize, SuggestedTariff,
  TariffRow, TariffCalculationType,
  getOriginPort, getDestinationPort, getContainerReference, getIncoterm,
  getTotalWeightFromBooking, getTotalVolumeFromBooking, calculateChargeableWeight,
  getTransportTypeId, estimateFclContainerLabelFromVolume,
} from "./invoiceFormHelpers";
import { BookingSelector } from "./form-sections";
import dayjs from "dayjs";

const { Text } = Typography;
const { Option } = Select;

interface Step1Props {
  form: FormInstance;
  selectedBooking: Booking | null;
  bookings: Booking[];
  isEditMode: boolean;
  initialBookingId?: number;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  tariffCalculationType: TariffCalculationType;
  setTariffCalculationType: (t: TariffCalculationType) => void;
  tariffRow: TariffRow;
  setTariffRow: React.Dispatch<React.SetStateAction<TariffRow>>;
  suggestedTariffs: SuggestedTariff[];
  setSuggestedTariffs: React.Dispatch<React.SetStateAction<SuggestedTariff[]>>;
  loadingSuggestions: boolean;
  selectedTariffId: number | null;
  setSelectedTariffId: (id: number | null) => void;
  containerSizes: ContainerSize[];
  handleBookingChange: (value: number | null) => void;
  addTariffToInvoiceLines: (priceOverride?: number, calculationTypeOverride?: TariffCalculationType) => void;
  fetchTariffSuggestions: (calculationTypeForApply?: TariffCalculationType) => void;
  isMatchingContainer: (size: string | null) => boolean;
  autogenerateInvoiceLines: (booking: Booking) => void;
  autogeneratingLines: boolean;
  autoSaveInvoice: (values: FormValues) => void;
  getBookingDisplayName: (b: Booking) => string;
}

const InvoiceWizardStep1Tariff: React.FC<Step1Props> = (props) => {
  const {
    form, selectedBooking, bookings, isEditMode, initialBookingId,
    formValues, setFormValues,
    tariffCalculationType, setTariffCalculationType,
    tariffRow, setTariffRow,
    suggestedTariffs, setSuggestedTariffs, loadingSuggestions,
    selectedTariffId, setSelectedTariffId,
    containerSizes, handleBookingChange,
    addTariffToInvoiceLines, fetchTariffSuggestions, isMatchingContainer,
    autogenerateInvoiceLines, autogeneratingLines,
    autoSaveInvoice, getBookingDisplayName,
  } = props;

  const isAirFreight = selectedBooking ? getTransportTypeId(selectedBooking) === 1 : false;

  const handleAddSpecialFees = () => {
    if (!selectedBooking) return;
    const currentLines: InvoiceLine[] = form.getFieldValue("lines") || [];
    const available: Array<{ key: string; label: string; value: number }> = [];

    if (!currentLines.some((l) => l.cost_type === "export_clearance"))
      available.push({ key: "export_clearance", label: "Export Clearance License Fee (€45)", value: 45 });
    if (!currentLines.some((l) => l.cost_type === "pickup_fee"))
      available.push({ key: "pickup_fee", label: `Pick-up Fee (€${selectedBooking.pick_up_fee || "Enter amount"})`, value: selectedBooking.pick_up_fee && selectedBooking.pick_up_fee > 0 ? selectedBooking.pick_up_fee : 0 });
    if (getEffectiveCommodityInspection(selectedBooking) && !currentLines.some((l) => l.cost_type === "commodity_inspection"))
      available.push({ key: "commodity_inspection", label: `Commodity Inspection (€${COMMODITY_INSPECTION_FEE_EUR})`, value: COMMODITY_INSPECTION_FEE_EUR });

    const pelFee = getPostExportLicenseFee(selectedBooking);
    if (!currentLines.some((l) => l.cost_type === "post_export_license")) {
      available.push({
        key: "post_export_license",
        label: pelFee != null ? `Post Export License (€${pelFee})` : "Post Export License (Manual Entry)",
        value: pelFee ?? 0,
      });
    }

    if (available.length === 0) { message.info("All special fees are already included"); return; }

    let selectedFees: string[] = [];
    Modal.confirm({
      title: "Select Special Fees to Add",
      content: (
        <Checkbox.Group
          options={available.map((f) => ({ label: f.label, value: f.key }))}
          onChange={(vals: string[]) => { selectedFees = vals; }}
        />
      ),
      onOk: () => {
        if (selectedFees.length === 0) { message.info("No fees selected"); return; }
        const newLines = [...currentLines];
        selectedFees.forEach((key) => {
          const fee = available.find((f) => f.key === key);
          if (fee) {
            newLines.push({
              id: 0, description: fee.label.split(" (€")[0],
              quantity: 1, unit_price: fee.value, vat_percentage: 0, cost_type: key,
            });
          }
        });
        form.setFieldsValue({ lines: newLines });
        setFormValues((prev) => ({ ...prev, lines: newLines }));
        message.success(`Added special fees: ${selectedFees.join(", ")}`);
      },
      okText: "Add Selected Fees",
    });
  };

  return (
    <>
      {!initialBookingId && (
        <BookingSelector
          bookings={bookings}
          onBookingChange={handleBookingChange}
          isEditMode={isEditMode}
          getBookingDisplayName={getBookingDisplayName}
        />
      )}

      {isEditMode && (
        <Form.Item name="invoice_number" label="Invoice Number">
          <Input disabled />
        </Form.Item>
      )}

      {selectedBooking && (
        <Card size="small" style={{ marginBottom: 8 }}>
          <Row gutter={8}>
            <Col span={8}>
              <Text style={{ fontSize: "11px" }}>
                <Text strong>Reference:</Text> {selectedBooking.reference || `EFF${selectedBooking.id}`}
                {selectedBooking.transport_type_info && (Array.isArray(selectedBooking.transport_type_info)
                  ? selectedBooking.transport_type_info[0]?.abbreviation
                  : selectedBooking.transport_type_info.abbreviation)}
                <br />
                <Text strong>Transport:</Text>{" "}
                {selectedBooking.transport_type_info && (Array.isArray(selectedBooking.transport_type_info)
                  ? selectedBooking.transport_type_info[0]?.name : selectedBooking.transport_type_info.name)}
                <br />
                {(() => {
                  const ttId = getTransportTypeId(selectedBooking);
                  if (ttId === 2 && selectedBooking.booking_type_info) {
                    const bt = Array.isArray(selectedBooking.booking_type_info)
                      ? selectedBooking.booking_type_info[0]?.name : selectedBooking.booking_type_info.name;
                    if (bt) return <><Text strong>Booking Type:</Text> {bt}<br /></>;
                  }
                  return null;
                })()}
                <Text strong>Company:</Text>{" "}
                {selectedBooking.company_info && (Array.isArray(selectedBooking.company_info)
                  ? selectedBooking.company_info[0]?.name : selectedBooking.company_info.name)}
                <br />
                {(() => {
                  const inc = getIncoterm(selectedBooking);
                  return inc ? <><Text strong>Incoterm:</Text> {inc.name}<br /></> : null;
                })()}
              </Text>
            </Col>

            <Col span={8}>
              <Text style={{ fontSize: "11px" }}>
                <Text strong>Origin:</Text>{" "}
                {(() => {
                  const o = getOriginPort(selectedBooking);
                  return <>{o.name}{o.isFromContainer && <Tag color="blue" style={{ marginLeft: 4, fontSize: "9px" }}>Container</Tag>}</>;
                })()}
                <br />
                <Text strong>Destination:</Text>{" "}
                {(() => {
                  const d = getDestinationPort(selectedBooking);
                  return <>{d.name}{d.isFromContainer && <Tooltip title={`From container ${d.containerRef || ""}`}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}</>;
                })()}
                <br />
                {(() => {
                  const ref = getContainerReference(selectedBooking);
                  return ref ? <><Text strong>Container:</Text> {ref}<br /></> : null;
                })()}
                {(() => {
                  if (!selectedBooking.container_info) return null;
                  const c = Array.isArray(selectedBooking.container_info) ? selectedBooking.container_info[0] : selectedBooking.container_info;
                  return <>
                    {c?.etd && <><Text strong>ETD:</Text> {dayjs(c.etd).format("YYYY-MM-DD")}<br /></>}
                    {c?.eta && <><Text strong>ETA:</Text> {dayjs(c.eta).format("YYYY-MM-DD")}<br /></>}
                  </>;
                })()}
              </Text>
            </Col>

            <Col span={8}>
              <Text style={{ fontSize: "11px" }}>
                <Text strong>Weight:</Text> {getTotalWeightFromBooking(selectedBooking).toFixed(2)} kg<br />
                {(() => {
                  const volume = getTotalVolumeFromBooking(selectedBooking);
                  const bookingTypeName = selectedBooking.booking_type_info
                    ? Array.isArray(selectedBooking.booking_type_info)
                      ? selectedBooking.booking_type_info[0]?.name
                      : selectedBooking.booking_type_info.name
                    : null;
                  const estSize =
                    bookingTypeName === "FCL"
                      ? estimateFclContainerLabelFromVolume(volume)
                      : null;
                  return (
                    <>
                      <Text strong>Volume:</Text> {volume.toFixed(2)} m³
                      {estSize && (
                        <Text type="secondary">
                          {" "}
                          (est. {estSize})
                        </Text>
                      )}
                      <br />
                    </>
                  );
                })()}
                {isAirFreight && (() => {
                  const { chargeableWeight } = calculateChargeableWeight(selectedBooking);
                  return <>
                    <Text strong>Chargeable:</Text>{" "}
                    <Text type="danger">{chargeableWeight.toFixed(2)} kg</Text>
                    <Tooltip title="For air freight, we use the higher of actual weight and volumetric weight (1 CBM = 167 kg)">
                      <InfoCircleOutlined style={{ marginLeft: 4, fontSize: "9px" }} />
                    </Tooltip>
                    <br />
                  </>;
                })()}
                <Text strong>Products:</Text>{" "}
                {selectedBooking.product_info
                  ? Array.isArray(selectedBooking.product_info) ? selectedBooking.product_info.length : 1
                  : 0} items
              </Text>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ margin: "8px 0 4px", fontSize: 11 }}>
            Origin fees &amp; services
          </Divider>
          <Row gutter={[12, 0]}>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Export clearance</Text>
              {selectedBooking.export_clearance ? (
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 14 }} />
              )}
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Commodity inspection</Text>
              {getEffectiveCommodityInspection(selectedBooking) ? (
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 14 }} />
              )}
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Pick up fee</Text>
              <Text style={{ fontSize: 11 }}>
                {selectedBooking.pick_up_fee && selectedBooking.pick_up_fee > 0
                  ? `€${selectedBooking.pick_up_fee.toFixed(2)}`
                  : "-"}
              </Text>
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Post export license</Text>
              <Text style={{ fontSize: 11 }}>
                {(() => {
                  const fee = getPostExportLicenseFee(selectedBooking);
                  return fee != null ? `€${fee.toFixed(2)}` : "-";
                })()}
              </Text>
            </Col>
          </Row>
        </Card>
      )}

      {/* Pricing Templates Summary */}
      {(() => {
        const lines = formValues.lines || [];
        const templateLines = lines.filter((l) => l.description?.includes("(from "));
        const specialFeeLines = lines.filter((l) =>
          ["export_clearance", "pickup_fee", "commodity_inspection", "post_export_license"].includes(l.cost_type || "")
        );
        if (templateLines.length === 0 && specialFeeLines.length === 0) return null;
        return (
          <Card size="small" style={{ marginBottom: 12, border: "1px solid #52c41a", backgroundColor: "#f6ffed" }}
            title={<span style={{ color: "#52c41a", fontWeight: "bold", fontSize: "14px" }}>Pricing Templates & Special Fees</span>}>
            {templateLines.length > 0 && <><Text strong>Templates:</Text> {templateLines.length} line(s) generated<br /></>}
            {specialFeeLines.length > 0 && (
              <><Text strong>Special Fees:</Text>{" "}
                {specialFeeLines.map((l) => {
                  if (l.cost_type === "export_clearance") return "Export Clearance (€45)";
                  if (l.cost_type === "pickup_fee") return `Pick-up Fee (€${l.unit_price})`;
                  if (l.cost_type === "commodity_inspection") return `Commodity Inspection (€${l.unit_price})`;
                  if (l.cost_type === "post_export_license") return `Post Export License (€${l.unit_price})`;
                  return "";
                }).filter(Boolean).join(", ")}
              </>
            )}
          </Card>
        );
      })()}

      {/* Tariff Calculation Card */}
      {selectedBooking && (
        <Card size="small" style={{ marginBottom: 12, border: "2px solid #1890ff", backgroundColor: "#f0f8ff" }}
          title={<span style={{ color: "#1890ff", fontWeight: "bold", fontSize: "14px" }}>Tariff Calculation</span>}>
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item label="Transport Type" style={{ marginBottom: 4 }}>
                <Select value={tariffCalculationType} onChange={setTariffCalculationType} style={{ width: "100%" }} size="small">
                  {isAirFreight
                    ? <Option value="per_kg">Air Freight (per KG)</Option>
                    : [
                        <Option key="per_cbm" value="per_cbm">Sea Freight (per CBM)</Option>,
                        <Option key="per_container" value="per_container">Sea Freight (per Container)</Option>,
                      ]}
                </Select>
              </Form.Item>
            </Col>
            {tariffCalculationType === "per_container" && (
              <Col span={5}>
                <Form.Item label="Container Type" style={{ marginBottom: 4 }}>
                  <Select placeholder="Select" style={{ width: "100%" }} size="small"
                    value={tariffRow.container_type_id}
                    onChange={(v) => { setTariffRow((p) => ({ ...p, container_type_id: v })); if (suggestedTariffs.length > 0) setSuggestedTariffs([...suggestedTariffs]); }}>
                    {containerSizes.map((s) => (
                      <Option key={s.id} value={s.id}>
                        {s.name}
                        {suggestedTariffs.length > 0 && <span style={{ marginLeft: 8, fontSize: "11px", color: "#1890ff" }}>({suggestedTariffs.filter((t) => t.containerSize === s.name).length})</span>}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={tariffCalculationType === "per_container" ? 4 : 5}>
              <Form.Item label="Quantity" style={{ marginBottom: 4 }}>
                <InputNumber
                  value={tariffRow.use_manual_quantity && tariffRow.manual_quantity !== undefined
                    ? tariffRow.manual_quantity
                    : tariffCalculationType === "per_kg" ? calculateChargeableWeight(selectedBooking).chargeableWeight
                    : tariffCalculationType === "per_cbm" ? getTotalVolumeFromBooking(selectedBooking)
                    : 1}
                  disabled={!tariffRow.use_manual_quantity}
                  onChange={(v) => setTariffRow((p) => ({ ...p, manual_quantity: v || 0 }))}
                  style={{ width: "100%" }}
                  size="small"
                  precision={tariffCalculationType === "per_container" ? 0 : 3}
                  formatter={(v) => {
                    const n = v == null || v === "" ? v : Number(Number(v).toFixed(3));
                    return tariffCalculationType === "per_kg" ? `${n} kg` : tariffCalculationType === "per_cbm" ? `${n} m³` : `${n}`;
                  }}
                  parser={(v) => parseFloat((v || "0").replace(/ (kg|m³)/g, "")) as unknown as 0}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="Unit Price" style={{ marginBottom: 4 }}>
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} size="small"
                  value={tariffRow.unit_price}
                  disabled={!!selectedTariffId && !tariffRow.use_manual_quantity}
                  onFocus={(e) => { if (tariffRow.unit_price === 0) e.target.select(); if (selectedBooking && suggestedTariffs.length === 0) fetchTariffSuggestions(); }}
                  onChange={(v) => setTariffRow((p) => ({ ...p, unit_price: v || 0 }))}
                  formatter={(v) => { const n = String(v ?? "0"); return `€${n}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","); }}
                  parser={(v) => parseFloat(String(v ?? "0").replace(/€\s?|(,*)/g, "")) as unknown as 0}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="VAT %" style={{ marginBottom: 4 }}>
                <InputNumber min={0} max={100} value={tariffRow.vat_percentage}
                  disabled={!!selectedTariffId && !tariffRow.use_manual_quantity}
                  onChange={(v) => setTariffRow((p) => ({ ...p, vat_percentage: v || 0 }))} style={{ width: "100%" }} size="small" />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item label="&nbsp;" style={{ marginBottom: 4 }}>
                <Button type="primary" onClick={() => addTariffToInvoiceLines()} style={{ width: "100%" }} size="small"
                  disabled={tariffRow.unit_price === 0 || (!!selectedTariffId && !tariffRow.use_manual_quantity)}>Add</Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <label style={{ fontSize: "11px", cursor: "pointer" }}>
                <input type="checkbox" checked={tariffRow.use_manual_quantity}
                  onChange={(e) => setTariffRow((p) => ({
                    ...p, use_manual_quantity: e.target.checked,
                    manual_quantity: e.target.checked
                      ? (tariffCalculationType === "per_kg" ? calculateChargeableWeight(selectedBooking).chargeableWeight
                        : tariffCalculationType === "per_cbm" ? getTotalVolumeFromBooking(selectedBooking) : 1)
                      : undefined,
                  }))} style={{ marginRight: 4 }} />
                Manual override
              </label>
            </Col>
            <Col span={12}>
              <Text strong style={{ fontSize: "12px", textAlign: "center", display: "block" }}>
                Total: € {(() => {
                  let qty = 1;
                  if (tariffRow.use_manual_quantity && tariffRow.manual_quantity !== undefined) { qty = tariffRow.manual_quantity; }
                  else if (tariffCalculationType === "per_kg") { qty = calculateChargeableWeight(selectedBooking).chargeableWeight; }
                  else if (tariffCalculationType === "per_cbm") { qty = getTotalVolumeFromBooking(selectedBooking); }
                  return (qty * tariffRow.unit_price).toFixed(2);
                })()}
              </Text>
            </Col>
          </Row>

          {/* Suggested Tariffs */}
          {suggestedTariffs.length > 0 && (
            <Row style={{ marginTop: 8 }}>
              <Col span={24}>
                <Text strong style={{ fontSize: "12px", color: "#1890ff" }}>Suggested Tariffs ({suggestedTariffs.length})</Text>
                <div style={{ maxHeight: 250, overflowY: "auto", border: "1px solid #d9d9d9", borderRadius: 6, padding: 8, marginTop: 4 }}>
                  {loadingSuggestions ? <Text type="secondary">Loading suggestions...</Text> : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                      {suggestedTariffs.map((t) => {
                        const isSelected = selectedTariffId === t.id;
                        const qty = tariffCalculationType === "per_kg"
                          ? calculateChargeableWeight(selectedBooking).chargeableWeight
                          : tariffCalculationType === "per_cbm"
                            ? getTotalVolumeFromBooking(selectedBooking)
                            : 1;
                        const estimatedTotal = qty * t.price;
                        return (
                          <Card key={t.id} size="small" hoverable
                            style={{
                              cursor: "pointer", borderRadius: 6, transition: "all 0.2s",
                              border: isSelected ? "2px solid #1890ff" : t.isSmartMatch ? "1px solid #52c41a" : "1px solid #e6f7ff",
                              backgroundColor: isSelected ? "#e6f7ff"
                                : (t.isSmartMatch && (tariffCalculationType !== "per_container" || !tariffRow.container_type_id || isMatchingContainer(t.containerSize || null)))
                                  ? "#f6ffed" : "#fafafa",
                            }}
                            bodyStyle={{ padding: 6 }}
                            onClick={() => {
                              setSelectedTariffId(t.id);
                              setTariffRow((p) => ({
                                ...p,
                                unit_price: t.price,
                                container_type_id: t.containerSizeId ?? p.container_type_id,
                              }));
                              addTariffToInvoiceLines(t.price, tariffCalculationType);
                            }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <Text strong style={{ fontSize: 16, color: "#1890ff" }}>€{t.price.toFixed(2)}</Text>
                              <Text type="secondary" style={{ fontSize: 10 }}>
                                /{tariffCalculationType === "per_kg" ? "kg" : tariffCalculationType === "per_cbm" ? "m³" : "container"}
                              </Text>
                              {t.isSmartMatch && <span style={{ fontSize: 10, background: "#52c41a", color: "white", padding: "2px 5px", borderRadius: 8, fontWeight: "bold" }}>✨</span>}
                            </div>
                            <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                              {qty.toFixed(2)} {tariffCalculationType === "per_kg" ? "kg" : tariffCalculationType === "per_cbm" ? "m³" : "×"} × €{t.price.toFixed(2)} = <Text strong style={{ fontSize: 11, color: "#000" }}>€{estimatedTotal.toFixed(2)}</Text>
                            </Text>
                            {t.bookingType && <Text style={{ fontSize: 11, fontWeight: 500, color: "#1890ff", display: "block", marginTop: 2 }}>{t.bookingType}</Text>}
                            {t.containerSize && (
                              <Text style={{ fontSize: 11, color: isMatchingContainer(t.containerSize) ? "#52c41a" : "#666", display: "block", fontWeight: isMatchingContainer(t.containerSize) ? "bold" : "normal" }}>
                                {isMatchingContainer(t.containerSize) ? "✓ " : ""}{t.containerSize}
                              </Text>
                            )}
                            {t.bracketDisplay && (
                              <div style={{ background: "#e6f7ff", border: "1px solid #91d5ff", borderRadius: 3, padding: "2px 4px", fontSize: 10, fontWeight: 500, color: "#0050b3", display: "inline-block", marginTop: 2 }}>
                                {t.bracketDisplay}
                              </div>
                            )}
                            {t.from && t.to && (
                              <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2 }}>
                                {new Date(t.from).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" })} – {new Date(t.to).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" })}
                              </Text>
                            )}
                            {t.matchReasons && t.matchReasons.length > 0 && (
                              <div style={{ marginTop: 3 }}>
                                {t.matchReasons.map((r, i) => (
                                  <span key={i} style={{ fontSize: 9, background: r === "volume bracket" ? "#52c41a" : "#e6f7ff", color: r === "volume bracket" ? "white" : "#1890ff", padding: "1px 4px", borderRadius: 2, marginRight: 2, fontWeight: 500 }}>
                                    {r}
                                  </span>
                                ))}
                              </div>
                            )}
                            <Button type={isSelected ? "primary" : "default"} size="small" block
                              style={{ fontSize: 10, fontWeight: 500, height: 20, padding: "0 4px", marginTop: 4 }}>
                              {isSelected ? "✓ Selected" : "Select"}
                            </Button>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Card>
      )}

      {/* Action buttons */}
      {selectedBooking && (
        <div style={{ marginBottom: 12 }}>
          <Button type="default" onClick={() => autogenerateInvoiceLines(selectedBooking)}
            loading={autogeneratingLines} block size="small" style={{ marginBottom: 4 }}>
            Regenerate Lines from Pricing Rules
          </Button>
          <Button type="default" onClick={handleAddSpecialFees} block size="small">
            Add Special Fees
          </Button>
        </div>
      )}
    </>
  );
};

export default InvoiceWizardStep1Tariff;
